/**
 * Game Service
 * Player-facing API calls for tables and gameplay.
 * Reads the JWT from localStorage and attaches it to every request.
 * Throws a plain Error on non-2xx responses so callers can show useful messages.
 */

import { getApiUrl, TOKEN_STORAGE_KEY } from '../config/app.config';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();

  const response = await fetch(`${getApiUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(
      body.message || `Request failed: ${response.status} ${response.statusText}`,
    );
  }

  // 204 No Content — return empty object
  if (response.status === 204) return {} as T;
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Types (mirror the backend DTOs we care about)
// ---------------------------------------------------------------------------

export interface TablePlayer {
  id: string;
  username: string;
  avatar: string | null;
  chipCount: number;
  seatPosition: number;
  isActive: boolean;
  isBot?: boolean;
}

export interface GameTable {
  id: string;
  name: string;
  maxPlayers: number;
  currentPlayers: number;
  minBuyIn: number;
  maxBuyIn: number;
  anteAmount: number;
  /** How the ante rises each hand (see components/Lobby/tableSpeeds anteRise). */
  anteIncrease?: number;
  anteGrowth?: string;
  anteEvery?: number;
  gridSize: number;
  status: 'WAITING' | 'ACTIVE' | 'PAUSED' | 'CLOSED';
  isPrivate: boolean;
  players: TablePlayer[];
  createdAt: string;
  updatedAt: string;
}

export interface TableListResponse {
  tables: GameTable[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateTablePayload {
  name: string;
  maxPlayers: number;
  minBuyIn: number;
  maxBuyIn: number;
  /** Every player pays it before each hand — the game has no blinds. */
  anteAmount: number;
  /** A table speed: sets the stakes on the server (see components/Lobby/tableSpeeds). */
  speed?: 'relaxed' | 'standard' | 'turbo';
  isPrivate?: boolean;
  password?: string;
}

export interface JoinTablePayload {
  // The server's JoinTableDto field.
  buyInAmount: number;
  password?: string;
}

export interface JoinTableResponse {
  tableId: string;
  seatPosition: number;
  chipCount: number;
}

export interface TableFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** List available tables with optional filters. */
async function listTables(filters: TableFilters = {}): Promise<TableListResponse> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.append(k, String(v));
  });
  const qs = params.toString();
  return request<TableListResponse>(`/tables${qs ? `?${qs}` : ''}`);
}

/** Get details for a single table. */
async function getTable(tableId: string): Promise<GameTable> {
  return request<GameTable>(`/tables/${tableId}`);
}

/** Create a new table. Returns the created table. */
async function createTable(payload: CreateTablePayload): Promise<GameTable> {
  return request<GameTable>('/tables', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Create (or rejoin) the caller's 1-human-vs-bots solo table. */
async function createSoloTable(): Promise<GameTable> {
  return request<GameTable>('/tables/solo', { method: 'POST' });
}

/** Join an existing table. */
async function joinTable(
  tableId: string,
  payload: JoinTablePayload,
): Promise<JoinTableResponse> {
  return request<JoinTableResponse>(`/tables/${tableId}/join`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Leave a table. */
async function leaveTable(tableId: string): Promise<void> {
  return request<void>(`/tables/${tableId}/leave`, { method: 'POST' });
}

export interface LobbyStats {
  you: { handsPlayed: number; handsWon: number; gamesWon: number; biggestWin: number };
  top: Array<{ username: string; gamesWon: number; handsWon: number; handsPlayed: number }>;
}

/** Your stats and the top players, for the lobby. */
async function getLobbyStats(): Promise<LobbyStats> {
  return request<LobbyStats>('/users/me/lobby');
}

export const gameService = {
  getLobbyStats,
  listTables,
  getTable,
  createTable,
  createSoloTable,
  joinTable,
  leaveTable,
};
